/**
 * PageDoc Converter Utility
 * 
 * Converts between PageDoc JSON format and MDX content for the Views API.
 * This enables the page builder to work with the existing Views backend.
 */

import { PageDoc, Section, Row, Column, Block } from '../types/page-builder.types';

/**
 * Convert PageDoc to MDX content
 */
export function convertPageDocToMDX(pageDoc: PageDoc): string {
  const sections = pageDoc.layout.map(section => convertSectionToMDX(section)).join('\n\n');
  
  return `# ${pageDoc.title}

${pageDoc.description ? `*${pageDoc.description}*\n\n` : ''}${sections}

<!-- PageDoc JSON -->
\`\`\`json
${JSON.stringify(pageDoc, null, 2)}
\`\`\``;
}

/**
 * Convert MDX content back to PageDoc
 */
export function convertMDXToPageDoc(mdxContent: string, fallbackData?: Partial<PageDoc>): PageDoc {
  // Try to extract embedded JSON first
  const jsonMatch = mdxContent.match(/```json\s*\n([\s\S]*?)\n```/);
  
  if (jsonMatch) {
    try {
      const extracted = JSON.parse(jsonMatch[1]);
      if (extracted.id && extracted.layout) {
        return extracted as PageDoc;
      }
    } catch (error) {
      console.warn('Failed to parse embedded PageDoc JSON:', error);
    }
  }

  // Fallback: parse MDX structure into PageDoc
  return parseMDXToPageDoc(mdxContent, fallbackData);
}

/**
 * Parse MDX content into PageDoc structure
 */
function parseMDXToPageDoc(mdxContent: string, fallbackData?: Partial<PageDoc>): PageDoc {
  const lines = mdxContent.split('\n');
  let title = 'Untitled Page';
  let description = '';
  
  // Extract title and description from MDX
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('# ')) {
      title = line.substring(2).trim();
    } else if (line.startsWith('*') && line.endsWith('*') && !line.includes('**')) {
      description = line.substring(1, line.length - 1).trim();
      break;
    }
  }

  return {
    _id: fallbackData?._id || `page-${Date.now()}`,
    orgId: fallbackData?.orgId || 'default-org',
    teamIds: fallbackData?.teamIds || [],
    slug: fallbackData?.slug || title.toLowerCase().replace(/\s+/g, '-'),
    status: fallbackData?.status || 'draft',
    title: fallbackData?.title || title,
    description: fallbackData?.description || description,
    version: fallbackData?.version || 1,
    createdBy: fallbackData?.createdBy || 'system',
    updatedBy: fallbackData?.updatedBy || 'system',
    createdAt: fallbackData?.createdAt || new Date().toISOString(),
    updatedAt: fallbackData?.updatedAt || new Date().toISOString(),
    layout: fallbackData?.layout || [],
  };
}

/**
 * Convert Section to MDX
 */
function convertSectionToMDX(section: Section): string {
  const sectionTag = getSectionTag(section.kind);
  const rows = section.rows.map(row => convertRowToMDX(row)).join('\n');
  
  if (sectionTag) {
    return `<${sectionTag}>
${rows}
</${sectionTag}>`;
  }
  
  return rows;
}

/**
 * Convert Row to MDX
 */
function convertRowToMDX(row: Row): string {
  if (row.columns.length === 1) {
    // Single column - no need for grid
    return convertColumnToMDX(row.columns[0]);
  }
  
  // Multi-column layout
  const columns = row.columns.map(column => {
    const width = column.width || 12;
    const content = convertColumnToMDX(column);
    return `  <Column width={${width}}>
${indentContent(content, 4)}
  </Column>`;
  }).join('\n');
  
  return `<Grid>
${columns}
</Grid>`;
}

/**
 * Convert Column to MDX
 */
function convertColumnToMDX(column: Column): string {
  return column.blocks.map(block => convertBlockToMDX(block)).join('\n\n');
}

/**
 * Convert Block to MDX
 */
function convertBlockToMDX(block: Block): string {
  const componentName = block.type;
  const props = formatPropsForMDX(block.props);
  
  // Self-closing components (no children)
  if (isSelfClosingComponent(block.type)) {
    return `<${componentName}${props} />`;
  }
  
  // Components with content
  const content = getBlockContent(block);
  if (content) {
    return `<${componentName}${props}>
${indentContent(content, 2)}
</${componentName}>`;
  }
  
  return `<${componentName}${props} />`;
}

/**
 * Format props for MDX JSX syntax
 */
function formatPropsForMDX(props: Record<string, any>): string {
  if (!props || Object.keys(props).length === 0) {
    return '';
  }
  
  const propStrings = Object.entries(props).map(([key, value]) => {
    if (value === undefined || value === null) {
      return null;
    }
    
    if (typeof value === 'boolean') {
      return value ? ` ${key}` : null;
    }
    
    if (typeof value === 'string') {
      return ` ${key}="${escapeQuotes(value)}"`;
    }
    
    if (typeof value === 'number') {
      return ` ${key}={${value}}`;
    }
    
    // Objects and arrays as expressions
    return ` ${key}={${JSON.stringify(value)}}`;
  }).filter(Boolean);
  
  return propStrings.join('');
}

/**
 * Get section tag based on kind
 */
function getSectionTag(kind?: string): string | null {
  switch (kind) {
    case 'hero': return 'Hero';
    case 'feature': return 'Feature';
    case 'content': return 'Section';
    case 'footer': return 'Footer';
    default: return null;
  }
}

/**
 * Check if component is self-closing
 */
function isSelfClosingComponent(type: string): boolean {
  const selfClosing = [
    'Image', 'Video', 'Divider', 'Spacer', 'Icon', 'Input', 'Checkbox', 'Switch'
  ];
  return selfClosing.includes(type);
}

/**
 * Get content from block for components that have children
 */
function getBlockContent(block: Block): string | null {
  switch (block.type) {
    case 'RichText':
      return (block.props.html as string) || (block.props.content as string) || '';
    // Hidden component - Button
    // case 'Button':
    //   return (block.props.text as string) || (block.props.label as string) || '';
    default:
      // Check for common content properties
      if (block.props.content && typeof block.props.content === 'string') {
        return block.props.content;
      }
      if (block.props.text && typeof block.props.text === 'string') {
        return block.props.text;
      }
      if (block.props.html && typeof block.props.html === 'string') {
        return block.props.html;
      }
      return null;
  }
}

/**
 * Indent content by specified number of spaces
 */
function indentContent(content: string, spaces: number): string {
  const indent = ' '.repeat(spaces);
  return content.split('\n').map(line => 
    line.trim() ? indent + line : line
  ).join('\n');
}

/**
 * Escape quotes in string values
 */
function escapeQuotes(str: string): string {
  return str.replace(/"/g, '\\"');
}

/**
 * Check if content looks like valid PageDoc JSON
 */
export function isValidPageDocJSON(content: string): boolean {
  try {
    const parsed = JSON.parse(content);
    return parsed && 
           typeof parsed._id === 'string' && 
           typeof parsed.title === 'string' && 
           Array.isArray(parsed.layout);
  } catch {
    return false;
  }
}

/**
 * Extract metadata from MDX content
 */
export function extractMetadataFromMDX(mdxContent: string): {
  title?: string;
  description?: string;
} {
  const lines = mdxContent.split('\n');
  let title: string | undefined;
  let description: string | undefined;
  
  for (let i = 0; i < Math.min(lines.length, 20); i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('# ') && !title) {
      title = line.substring(2).trim();
    } else if (line.startsWith('*') && line.endsWith('*') && !line.includes('**') && !description) {
      description = line.substring(1, line.length - 1).trim();
    }
    
    if (title && description) break;
  }
  
  return { title, description };
}

export default {
  convertPageDocToMDX,
  convertMDXToPageDoc,
  isValidPageDocJSON,
  extractMetadataFromMDX,
};